import { React, useState, useContext, useEffect, useCallback } from "react";
import { Button, Flex, KeyValue, PencilSimple, MNotification } from "@cisco/spaces-dash-lib-styleguide";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./location-info.scss";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { I18nContext } from "../../contexts/i18nContext";
import { getLocation2SyncStatus, listApPrefix, updateLocationInfo } from "../../service/business-hierarchy-service";
import { getTimeZone } from "../../service/timezone-service";
import nodeTypeImg from "../../assets/images/location-info/node_type.svg";
import networkReferenceImg from "../../assets/images/location-info/network_reference.svg";
import syncStatusImg from "../../assets/images/location-info/sync_status.svg";
import lastSyncImg from "../../assets/images/location-info/last_sync.svg";
import LocationEditPopup from "./location-edit-popup";
import useNotification from "../../hooks/useNotification";
import useLoader from "../../hooks/useLoader";
import LocationEditPrefixDrawer from "./location-edit-prefix-drawer";

function LocationInfo(props) {
  const i18n = useContext(I18nContext);
  const { setToastMessage } = useNotification();
  const { setLoading } = useLoader();
  const { node, selectedNodeDetails, setModalOpened, selectedNodeLocationCounts, googleMapApiKey } = props;
  const [invalidLocation, setInvalidLocation] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [editingNode, setEditingNode] = useState({
    name: "",
    brandName: "",
    address: "",
    timeZoneId: "",
    totalAreaValue: "",
    totalCapacity: "",
    totalAreaUnit: "Square Feet",
    latitude: null,
    longitude: null,
    locationId: null
  });
  const [editingNodeBkUp, setEditingNodeBkUp] = useState({
    name: "",
    brandName: "",
    address: "",
    timeZoneId: "",
    totalAreaValue: "",
    totalCapacity: "",
    totalAreaUnit: "Square Feet",
    latitude: null,
    longitude: null,
    locationId: null
  });
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [timeZoneList, setTimeZoneList] = useState([]);
  const [apsData, setApsData] = useState([]);
  const [selectedAps, setSelectedAps] = useState([]);
  const [showEditPrefix, setShowEditPrefix] = useState(false);
  const [placeholder, setPlaceholder] = useState({
    BrandName: "",
    Country: "",
    State: "",
    City: "",
    Address: "",
    Zipcode: "",
    TotalAreaValue: 0,
    TotalAreaUnit: "",
    TotalCapacity: "",
    timeZoneId: ""
  });
  const unitList = [
    {
      label: i18n.t("hierarchy-nodes.square-feet-text"),
      value: i18n.t("hierarchy-nodes.square-feet-text")
    },
    {
      label: i18n.t("hierarchy-nodes.square-meter-text"),
      value: i18n.t("hierarchy-nodes.square-meter-text")
    }
  ];
  const wirelessText = i18n.t("business-location-details.wireless-controller-text");
  const naText = i18n.t("hierarchy-nodes.na-text");

  const getApPrefixList = useCallback(() => {
    if (node && node.data && node.data.id) {
      const payload = {
        locationId: node.data.id,
        type: "list",
        newPrefix: null,
        addedPrefixes: []
      };
      listApPrefix(payload)
        .then((data) => {
          if (data.status && data.data) {
            setApsData(data.data);
          } else {
            setApsData([]);
            // setToastMessage("Unable to fetch AP List", 5000, "negative");
          }
        })
        .catch((error) => {
          setApsData([]);
          console.error(error);
          // setToastMessage("Unable to fetch AP List", 5000, "negative");
        });
    }
  }, [node]);

  useEffect(() => {
    setTimeZoneList(getTimeZone());
  }, []);

  const getNodeType = () => {
    if (node && node.data && node.data.type && node.data.type.length) {
      if (node.data.type.length > 1) {
        if (node.data.type.indexOf("CAMPUS") > -1 && (node.data.type.indexOf("ORG") > -1 || node.data.type.indexOf("MERAKI") > -1)) {
          return "MERAKI_CAMPUS";
        }
        return node.data.type[0];
      }
      return node.data.type[0];
    }
    return null;
  };

  const showEditLocationPopup = () => {
    setEditingNodeBkUp({ ...editingNode });
    setModalOpened(true);
    setShowEditPopup(true);
  };

  const hideEditLocationPopup = () => {
    setEditingNode({ ...editingNodeBkUp });
    setModalOpened(false);
    setShowEditPopup(false);
  };

  const validateFields = () => {
    if (node && node.data && node.data.type && node.data.type[0]) {
      const type = node.data.type[0];
      if (editingNode.name && editingNode.name.trim() !== "") {
        return true;
      }

      if (editingNode.brandName && editingNode.brandName.trim() !== "") {
        return true;
      }

      if (editingNode.totalAreaValue && editingNode.totalAreaValue.toString().trim() !== "") {
        return true;
      }

      if (editingNode.totalAreaUnit && editingNode.totalAreaUnit !== "") {
        return true;
      }

      if (editingNode.totalCapacity && editingNode.totalCapacity !== "") {
        return true;
      }

      if (editingNode.address && editingNode.address !== "" && editingNode.address.trim() !== "" && type !== "ROOT") {
        return true;
      }

      if (editingNode.latitude && editingNode.latitude !== "" && type !== "ROOT") {
        return true;
      }

      if (editingNode.longitude && editingNode.longitude !== "" && type !== "ROOT") {
        return true;
      }

      if (editingNode.timeZoneId && editingNode.timeZoneId !== "" && editingNode.timeZoneId.trim() !== "") {
        return true;
      }
    }

    return false;
  };

  const validateTotalArea = () => {
    if (editingNode.totalAreaValue) {
      const totalAr = parseFloat(editingNode.totalAreaValue);
      if (totalAr <= 0) {
        setToastMessage(i18n.t("location-info.valid-value-for-total-area"), 5000, "negative");
        return false;
      }
      if (!editingNode.totalAreaUnit) {
        setToastMessage(i18n.t("location-info.provide-total-area"), 5000, "negative");
        return false;
      }
    }

    if (editingNode.totalCapacity) {
      const totalCap = parseFloat(editingNode.totalCapacity);
      if (totalCap <= 0) {
        setToastMessage(i18n.t("location-info.valid-value-for-capacity"), 5000, "negative");
        return false;
      }
    }

    if (editingNode.totalAreaUnit && editingNode.totalAreaUnit === "Square Feet") {
      const totalArea = parseFloat(editingNode.totalAreaValue);
      if (totalArea !== undefined && totalArea !== null && (totalArea < 100 || totalArea > 1000000)) {
        setToastMessage(i18n.t("location-info.enter-value-between-square-feet-msg"), 5000, "negative");
        return false;
      }
      // valid input
      return true;
    }
    if (editingNode.totalAreaUnit && editingNode.totalAreaUnit === "Square Meter") {
      const totalArea = parseFloat(editingNode.totalAreaValue);
      if (totalArea !== undefined && totalArea !== null && (totalArea < 9.2903 || totalArea > 92903.04)) {
        setToastMessage(i18n.t("location-info.enter-value-between-square-feet-msg"), 5000, "negative");
        return false;
      }
      // valid input
      return true;
    }
    // valid input
    return true;
  };

  const saveLocation = async () => {
    if (!validateTotalArea() || !validateFields()) {
      setToastMessage("Missing Some Fields", 5000, "negative");
      return;
    }

    if (editingNode.totalAreaValue && !editingNode.totalAreaUnit) {
      setToastMessage(i18n.t("location-info.provide-value-for-both-area-unit"), 5000, "negative");
      return;
    }

    if (editingNode.totalAreaUnit && !editingNode.totalAreaValue) {
      setToastMessage(i18n.t("location-info.provide-value-for-both-area-unit"), 5000, "negative");
      return;
    }

    if (editingNode.totalCapacity) {
      const totalCapacity = parseFloat(editingNode.totalCapacity);
      if (!Number.isInteger(totalCapacity) || totalCapacity <= 0) {
        setToastMessage(i18n.t("location-info.enter-valid-value-for-max-capacity"), 5000, "negative");
        return;
      }
    }

    if (editingNode.timeZoneId && editingNode.timeZoneId.trim() !== "") {
      if (!timeZoneList.map((tz) => tz.value).includes(editingNode.timeZoneId)) {
        setToastMessage(i18n.t("business-location-details.invalid-time-zone"), 5000, "negative");
        return;
      }
      // setToastMessage(i18n.t("business-location-details.time-zone-data-not-available-text"), 5000, "negative");
      // return;
    }

    // let address = document.getElementById("location-address")?.value;
    // if (address) {
    //   setEditingNode((prevState) => ({ ...prevState, address }));
    // }

    const payload = { ...editingNode };
    if (payload.latitude) {
      payload.latitude = parseFloat(payload.latitude);
    }
    if (payload.longitude) {
      payload.longitude = parseFloat(payload.longitude);
    }

    try {
      setLoading(true);
      const res = await updateLocationInfo(payload);
      if (res && res.status) {
        // this.node.data.name = this.editingNode.name;
        // this.node.data.brandName = this.editingNode.brandName;
        // this.node.data.totalArea = this.editingNode.totalAreaValue;
        // this.node.data.totalAreaUnit = this.editingNode.totalAreaUnit;
        // this.node.data.totalCapacity = this.editingNode.totalCapacity;
        // this.node.data.geoLocation = `${this.editingNode.latitude},${this.editingNode.longitude}`;
        // this.node.data.address = this.editingNode.address;
        // this.node.data.inferredTimeZoneId = this.editingNode.timeZoneId;
        setEditingNodeBkUp({ ...editingNode });
        hideEditLocationPopup();
        setLoading(false);
        setToastMessage(`${editingNode.name} ${i18n.t("location-info.updated successfully")}`, 5000, "positive");
        selectedNodeLocationCounts();
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      setToastMessage(i18n.t("location-info.error-on-update-location"), 5000, "negative");
    }
  };

  const canShowSection = () => {
    if (node && node.data && node.data.type && node.data.type[0] !== "ROOT" && node.data.type[0] !== "GROUP") {
      return true;
    }
    return false;
  };

  const getValue = (key) => {
    if (selectedNodeDetails) {
      switch (key) {
        case "brandName":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.BrandName) {
            return {
              value: placeholder.BrandName,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "country":
          if (node && node.data && node.data[key]) {
            return {
              value: node.data[key],
              placeholder: false
            };
          }
          if (placeholder.Country) {
            return {
              value: placeholder.Country,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "state":
          if (node && node.data && node.data[key]) {
            return {
              value: node.data[key],
              placeholder: false
            };
          }
          if (placeholder.State) {
            return {
              value: placeholder.State,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "city":
          if (node && node.data && node.data[key]) {
            return {
              value: node.data[key],
              placeholder: false
            };
          }
          if (placeholder.City) {
            return {
              value: placeholder.City,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "zipcode":
          if (node && node.data && node.data[key]) {
            return {
              value: node.data[key],
              placeholder: false
            };
          }
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "totalArea":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.TotalAreaValue) {
            return {
              value: placeholder.TotalAreaValue,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "totalAreaUnit":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.TotalAreaUnit) {
            return {
              value: placeholder.TotalAreaUnit,
              placeholder: true
            };
          }

          return {
            value: null,
            placeholder: false
          };

        case "totalCapacity":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.TotalCapacity) {
            return {
              value: placeholder.TotalCapacity,
              placeholder: true
            };
          }

          return {
            value: null,
            placeholder: false
          };

        case "address":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.Address) {
            return {
              value: placeholder.Address,
              placeholder: true
            };
          }

          return {
            value: null,
            placeholder: false
          };

        case "latitude":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "longitude":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          return {
            value: null,
            placeholder: false
          };

        case "timeZone":
          if (selectedNodeDetails[key]) {
            return {
              value: selectedNodeDetails[key],
              placeholder: false
            };
          }
          if (placeholder.timeZoneId) {
            return {
              value: placeholder.timeZoneId,
              placeholder: true
            };
          }
          return {
            value: null,
            placeholder: false
          };

        default:
          return null;
      }
    } else {
      return null;
    }
  };

  const showEditPrefixPopup = () => {
    if (node && node.data && node.data.id) {
      const payload = {
        locationId: node.data.id,
        type: "apcount",
        newPrefix: null,
        addedPrefixes: []
      };
      setSelectedAps([]);
      setShowEditPrefix(true);

      listApPrefix(payload)
        .then((data) => {
          if (data.status && data.data) {
            if (data.data.apPrefixCount && Object.keys(data.data.apPrefixCount)) {
              const newAps = Object.keys(data.data.apPrefixCount).map((ap) => ({
                key: ap,
                count: data.data.apPrefixCount[ap] ? data.data.apPrefixCount[ap].length : 0
              }));
              setSelectedAps((prevAps) => [...prevAps, ...newAps]);
            }
          } else {
            setToastMessage("Unable to fetch AP List", 5000, "negative");
          }
        })
        .catch((error) => {
          console.error(error);
          setToastMessage("Unable to fetch AP List", 5000, "negative");
        });
    }
  };

  const getZipCode = () => {
    if (node && node.data && node.data.zipcode) {
      return node.data.zipcode;
    }
    if (selectedNodeDetails && selectedNodeDetails.zipcode) {
      return selectedNodeDetails.zipcode;
    }
    return null;
  };

  const getFormattedTime = (time) => {
    const numericTime = Number(time);
    if (time && !Number.isNaN(numericTime)) {
      return dayjs(numericTime * 1000).format("DD-MM-YYYY h:mm A");
    }
    return "";
  };

  const getFormattedValue = (key) => {
    if (key === "totalArea") {
      return (
        <span style={getValue(key).placeholder ? { color: "orange" } : {}}>
          {getValue(key).value || naText} {getValue(key).value && getValue("totalAreaUnit").value}
        </span>
      );
    }
    return <span style={getValue(key).placeholder ? { color: "orange" } : {}}>{getValue(key).value || naText}</span>;
  };

  const preparePlaceHolderObject = () => {
    const newPlaceHolder = {};
    if (selectedNodeDetails && selectedNodeDetails.placeHolder) {
      selectedNodeDetails.placeHolder.forEach((pHolder) => {
        if (pHolder.key === "timeZoneId") {
          newPlaceHolder[pHolder.key] = pHolder.value;
        } else {
          newPlaceHolder[pHolder.key] = pHolder.value[0] ? pHolder.value[0] : pHolder.value;
        }
      });
    }
    setPlaceholder(newPlaceHolder);
  };

  useEffect(() => {
    getApPrefixList();
    if (node && node.data && node.data.id) {
      getLocation2SyncStatus(node.data.id)
        .then((data) => {
          if (data.status && data.data) {
            setSyncStatus(data.data);
          } else {
            setSyncStatus(null);
          }
        })
        .catch((error) => {
          setSyncStatus(null);
          console.error("Error::", error);
        });
    }
  }, [getApPrefixList, node]);

  useEffect(() => {
    let editingNodeCopy = {};
    if (selectedNodeDetails) {
      editingNodeCopy = {
        ...editingNodeCopy,
        name: selectedNodeDetails.name,
        brandName: selectedNodeDetails.brandName,
        address: selectedNodeDetails.address,
        timeZoneId: selectedNodeDetails.timeZone,
        totalAreaValue: selectedNodeDetails.totalArea,
        totalCapacity: selectedNodeDetails.totalCapacity,
        totalAreaUnit: selectedNodeDetails.totalAreaUnit,
        latitude: selectedNodeDetails.latitude,
        longitude: selectedNodeDetails.longitude,
        locationId: selectedNodeDetails.id
      };
      preparePlaceHolderObject();
    } else {
      setInvalidLocation(true);
    }
    setEditingNode(editingNodeCopy);
  }, [selectedNodeDetails]);

  return (
    <div className="loc-info-container">
      <Flex className="loc-info-status" direction="vertical" style={{ display: syncStatus ? "block" : "none" }}>
        <Flex>
          <div className="status-block">
            <div className="icon-block">
              <img src={nodeTypeImg} alt="Location" />
            </div>
            <div className="info-block">
              {/* <div className="info-label">{i18n.t("location-info.node-type-caps-text")}</div>
              <div className="info-value">{getNodeType() === "Wlc" || getNodeType() === "wlc" || getNodeType() === "WLC" ? wirelessText : getNodeType()}</div> */}
              <KeyValue placement="above">
                <KeyValue.Pair pair={[i18n.t("location-info.node-type-caps-text"), getNodeType() === "Wlc" || getNodeType() === "wlc" || getNodeType() === "WLC" ? wirelessText : getNodeType()]} />
              </KeyValue>
            </div>
          </div>
          <div className="status-block">
            <div className="icon-block">
              <img src={networkReferenceImg} alt="Network" />
            </div>
            <div className="info-block">
              {/* <div className="info-label">{i18n.t("location-info.network-reference-text")}</div>
              <div className="info-value">{syncStatus?.networkReference}</div> */}
              <KeyValue placement="above">
                <KeyValue.Pair pair={[i18n.t("location-info.network-reference-text"), syncStatus?.networkReference]} />
              </KeyValue>
            </div>
          </div>
          <div className="status-block" style={{ display: syncStatus && !syncStatus?.error ? "block" : "none" }}>
            <div className="icon-block">
              <img src={syncStatusImg} alt="Sync" />
            </div>
            <div className="info-block">
              {/* <div className="info-label">{i18n.t("location-info.sync-status-text")}</div>
              <div className="info-value">{syncStatus?.status}</div> */}
              <KeyValue placement="above">
                <KeyValue.Pair pair={[i18n.t("location-info.sync-status-text"), syncStatus?.status]} />
              </KeyValue>
            </div>
          </div>
          <div className="status-block" style={{ display: syncStatus && !syncStatus?.error ? "block" : "none" }}>
            <div className="icon-block">
              <img src={lastSyncImg} alt="Sync" />
            </div>
            <div className="info-block">
              {/* <div className="info-label">{i18n.t("location-info.last-synced-text")}</div>
              <div className="info-value">{getFormattedTime(syncStatus?.lastSync)}</div> */}
              <KeyValue placement="above">
                <KeyValue.Pair pair={[i18n.t("location-info.last-synced-text"), getFormattedTime(syncStatus?.lastSync)]} />
              </KeyValue>
            </div>
          </div>
        </Flex>
        {apsData && apsData.length !== 0 && (
          <div className="ap-prefix-wrapper">
            <div className="info-heading" style={{ marginLeft: 0 }}>
              {i18n.t("location-info.access-points-prefix-used-text")}
              {node && node.data && node.data.access === "Full" && (
                <Button icon={<PencilSimple size={16} weight="bold" />} kind="tertiary" onClick={showEditPrefixPopup} style={{ marginLeft: "10px" }} id="prefix-edit">
                  {` ${i18n.t("location-info.add-edit-text")}`}
                </Button>
              )}
            </div>
            <div className="ap-prefix-list">
              {apsData.map((prefix) => (
                <span key={uuidv4()} className="ap-prefix">
                  {prefix}
                </span>
              ))}
            </div>
          </div>
        )}
        {selectedNodeDetails && (
          <div className="loc-info-stats">
            <Flex className="info-heading">
              {i18n.t("location-info.location-data-text")}
              {node && node.data && node.data.access === "Full" && (
                <Button icon={<PencilSimple size={16} weight="bold" />} kind="tertiary" onClick={showEditLocationPopup} style={{ marginLeft: "10px" }} id="location-edit">
                  {` ${i18n.t("location-info.edit-text")}`}
                </Button>
              )}
            </Flex>
            <div>
              <MNotification status="warning">
                {i18n.t("location-info.some-location-inherited-text")}
                <span className="orange-color">{` ${i18n.t("location-info.orange-color-text")}`}</span> {` ${i18n.t("location-info.update-location-text")}`}
              </MNotification>
            </div>
            <KeyValue className="stats-container">
              <KeyValue.Group id="location-brand-area-limit">
                <KeyValue.Pair pair={[i18n.t("location-info.brand-text"), getFormattedValue("brandName")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.total-area-caps-text"), getFormattedValue("totalArea")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.occupancy-limit-text"), getFormattedValue("totalCapacity")]} />
                {canShowSection() && <KeyValue.Pair pair={[i18n.t("location-info.address-caps-text"), getFormattedValue("address")]} />}
                <KeyValue.Pair pair={[i18n.t("location-info.country-caps-text"), getFormattedValue("country")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.state-caps-text"), getFormattedValue("state")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.city-caps-text"), getFormattedValue("city")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.zipcode-caps-text"), getFormattedValue("zipcode")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.latitude-caps-text"), getFormattedValue("latitude")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.longitude-caps-text"), getFormattedValue("longitude")]} />
                <KeyValue.Pair pair={[i18n.t("location-info.time-zone-caps-text"), getFormattedValue("timeZone")]} />
              </KeyValue.Group>
            </KeyValue>
          </div>
        )}
        {invalidLocation && !selectedNodeDetails && <MNotification status="warning">{`${i18n.t("location-info.verify-location-data-text")}`}</MNotification>}
      </Flex>

      {googleMapApiKey && (
        <LocationEditPopup
          googleMapApiKey={googleMapApiKey}
          showEditPopup={showEditPopup}
          cancelEdit={hideEditLocationPopup}
          saveLocation={saveLocation}
          editingNode={editingNode}
          setEditingNode={setEditingNode}
          validateTotalArea={validateTotalArea}
          unitList={unitList}
          canShowSection={canShowSection}
          node={node}
          getZipCode={getZipCode}
          timeZoneList={timeZoneList}
        />
      )}
      <LocationEditPrefixDrawer
        isOpen={showEditPrefix}
        onClose={() => setShowEditPrefix(false)}
        nodeId={node?.data?.id}
        nodeName={node?.data?.name}
        selectedAps={selectedAps}
        setSelectedAps={setSelectedAps}
        getApPrefixList={getApPrefixList}
      />
    </div>
  );
}
export default LocationInfo;
